import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Archive, ArrowRightLeft, Check, ChevronDown, Download, ExternalLink, FileText, Pencil, Play, RefreshCw, Undo2, Upload, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import ShareLinkManager from '../components/ShareLinkManager';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Menu from '../ui/Menu';
import Popover from '../ui/Popover';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import StageChip from '../ui/StageChip';
import Tabs from '../ui/Tabs';
import Tag from '../ui/Tag';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { useUploads, useUploadDone } from '../shell/uploads';
import { canSetProducer } from '../lib/filmedHold';

// Qual on src/ui (DESIGN rev 2.1 §3.1: Grid at medium density, grouped by stage, Expanded-row panel).
// Same calls as before: GET /api/qual/scripts and /api/qual/videos (with their filters), GET /api/clients, GET /api/team-members;
// scripts: GET + PATCH /api/scripts/:id (summary note, scene cells, per-scene qual notes), PATCH /api/scripts/:id/status;
// videos: PATCH /api/live-production|ai-production/:id/status, GET /api/video-assets (current + archived), GET /:id/download,
// DELETE /:id (archives), POST + GET /api/downloads/zip, share links (ShareLinkManager), uploads (the global Upload sheet).
// Every script here is "Needs Review"; every video is in Qual. A status change takes the row off the list, as before.

const FORMAT_COLS = {
  Skit:         [{ key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'notes', label: 'Notes' }, { key: 'scene', label: 'Scene' }, { key: 'props', label: 'Props' }],
  Longform:     [{ key: 'sceneNumber', label: 'Scene number' }, { key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'scene', label: 'Scene description' }],
  TH:           [{ key: 'narration', label: 'Narration' }, { key: 'aRoll', label: 'A roll' }, { key: 'bRoll', label: 'B roll' }, { key: 'vfx', label: 'VFX' }, { key: 'sfx', label: 'SFX' }, { key: 'props', label: 'Props' }],
  AI:           [{ key: 'narration', label: 'Narration' }, { key: 'visuals', label: 'Visuals' }],
  Mockumentary: [{ key: 'character', label: 'Character' }, { key: 'line', label: 'Line' }, { key: 'notes', label: 'Notes' }, { key: 'aroll', label: 'A roll' }, { key: 'broll', label: 'B roll' }, { key: 'vfx', label: 'FX' }, { key: 'props', label: 'Props' }, { key: 'location', label: 'Location' }],
};
const TABS = [{ id: 'scripts', label: 'Scripts' }, { id: 'videos', label: 'Videos' }];
const VIDEO_EXT = /\.(mp4|mov|webm|avi|mpeg|mpg)$/i;
const ACCEPTED_EXT = '.mp4,.mov,.webm,.avi,.mpeg,.mpg';
// Returning from the review page: VideoReviewPage reads this label and hands returnVideo back as state.openVideo.
const RETURN_LABEL = 'Video Qual Review';

// ── Helpers ───────────────────────────────────────────────────────────────────

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const isEpisode = v => v.episodeNumber != null && !!v.scriptId;
/** Video title: an episode reads "Series, Ep N: episode title" (was "Ep N, title" under a series header row). */
const videoTitle = v => (isEpisode(v)
  ? `${clean(v.name)}, Ep ${v.episodeNumber}: ${clean(v.episodeTitle) || `Episode ${v.episodeNumber}`}`
  : clean(v.name));
const statusEndpoint = v => (v.track === 'Live' ? `/api/live-production/${v.id}/status` : `/api/ai-production/${v.id}/status`);
// OUT-2.1 (DESIGN rev 2.3 §7): Qual refuses Approve on a card with no editor, so every approved minute has an editor.
// The frontend holds it (the API records either way); "Edit card" opens where the editor is set: a Live card's Edit stage,
// the AI list filtered to the card.
export const NEEDS_EDITOR = 'Set the editor before approving';
const hasEditor = v => !!(v.editorId || v.editor?.id);
const editCardPath = v => (v.track === 'Live' ? `/production/${v.id}?stage=edit` : `/ai?q=${encodeURIComponent(v.name || '')}`);
const memberHas = (m, role) => (m.roles || []).some(r => (r.role || r) === role);
const rowsFor = (text, width = 34) => Math.min(10, Math.max(1, String(text || '').split('\n').reduce((n, l) => n + Math.max(1, Math.ceil(l.length / width)), 0)));

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Qual() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();

  const tab = searchParams.get('tab') === 'videos' ? 'videos' : 'scripts';
  // Other params (filters, ?video=) stay when the tab changes, so deep links and the review round trip keep working.
  const setTab = t => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });

  const canEdit = user?.roles?.some(r => ['Admin', 'Power User', 'Qual Reviewer'].includes(r));

  // Filters seed from the URL so the back-from-player round trip and a refresh restore them.
  const [sClientFilter,   setSClientFilter]   = useState(() => searchParams.get('sClient')   || '');
  const [sWriterFilter,   setSWriterFilter]   = useState(() => searchParams.get('sWriter')   || '');
  const [sReviewerFilter, setSReviewerFilter] = useState(() => searchParams.get('sReviewer') || '');
  const [vClientFilter,   setVClientFilter]   = useState(() => searchParams.get('vClient')   || '');
  const [vTrackFilter,    setVTrackFilter]    = useState(() => searchParams.get('vTrack')    || '');
  const [vEditorFilter,   setVEditorFilter]   = useState(() => searchParams.get('vEditor')   || '');

  const [scripts,       setScripts]       = useState([]);
  const [scriptsState,  setScriptsState]  = useState('loading');
  const [videos,        setVideos]        = useState([]);
  const [videosState,   setVideosState]   = useState('loading');
  const [clients,       setClients]       = useState([]);
  const [members,       setMembers]       = useState([]);
  const [openScriptId,  setOpenScriptId]  = useState(null);
  const [openVideo,     setOpenVideo]     = useState(null);
  const [menuFor,       setMenuFor]       = useState(null);
  const [busyId,        setBusyId]        = useState(null);

  // Filter changes go back into the URL so any returnTo built downstream (panel → /review → back) keeps them.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const put = (k, v) => (v ? p.set(k, v) : p.delete(k));
      put('sClient', sClientFilter); put('sWriter', sWriterFilter); put('sReviewer', sReviewerFilter);
      put('vClient', vClientFilter); put('vTrack', vTrackFilter); put('vEditor', vEditorFilter);
      return p;
    }, { replace: true });
  }, [sClientFilter, sWriterFilter, sReviewerFilter, vClientFilter, vTrackFilter, vEditorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  const fetchScripts = useCallback(async () => {
    setScriptsState('loading');
    const params = new URLSearchParams();
    if (sClientFilter)   params.set('clientId',   sClientFilter);
    if (sWriterFilter)   params.set('writerId',   sWriterFilter);
    if (sReviewerFilter) params.set('reviewerId', sReviewerFilter);
    try {
      const r = await api.get(`/api/qual/scripts?${params}`);
      setScripts(r.data);
      setScriptsState('data');
    } catch { setScriptsState('error'); }
  }, [sClientFilter, sWriterFilter, sReviewerFilter]);

  const fetchVideos = useCallback(async () => {
    setVideosState('loading');
    const params = new URLSearchParams();
    if (vClientFilter) params.set('clientId', vClientFilter);
    if (vTrackFilter)  params.set('track',    vTrackFilter);
    if (vEditorFilter) params.set('editorId', vEditorFilter);
    try {
      const r = await api.get(`/api/qual/videos?${params}`);
      setVideos(r.data);
      setVideosState('data');
    } catch { setVideosState('error'); }
  }, [vClientFilter, vTrackFilter, vEditorFilter]);

  useEffect(() => { if (tab === 'scripts') fetchScripts(); }, [tab, fetchScripts]);
  useEffect(() => { if (tab === 'videos')  fetchVideos();  }, [tab, fetchVideos]);

  // Reopen the video panel after the player: location.state.openVideo carries the record; ?video=<id> is the
  // fallback (refresh or direct link), resolved once the videos load.
  const consumedReturnRef = useRef(false);
  useEffect(() => {
    if (consumedReturnRef.current) return;
    if (location.state?.openVideo) {
      setOpenVideo(location.state.openVideo);
      consumedReturnRef.current = true;
      const p = new URLSearchParams(searchParams);
      if (p.has('video')) { p.delete('video'); setSearchParams(p, { replace: true }); }
      return;
    }
    const openVideoId = searchParams.get('video');
    if (!openVideoId || videos.length === 0) return;
    const v = videos.find(x => x.id === openVideoId);
    if (v) { setOpenVideo(v); consumedReturnRef.current = true; }
    const p = new URLSearchParams(searchParams);
    p.delete('video');
    setSearchParams(p, { replace: true });
  }, [videos, searchParams, setSearchParams, location.state]);

  // ── Inline status changes (the old status dropdown in each row) ─────────────

  async function setScriptStatus(s, status) {
    setMenuFor(null);
    setBusyId(s.id);
    try {
      await api.patch(`/api/scripts/${s.id}/status`, { status });
      setScripts(prev => prev.filter(x => x.id !== s.id));
      if (openScriptId === s.id) setOpenScriptId(null);
      toast(status === 'Approved' ? `${clean(s.scriptName)} approved.` : `${clean(s.scriptName)} moved back to in progress.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The status did not change. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  async function setVideoStatus(v, status) {
    setBusyId(v.id);
    try {
      await api.patch(statusEndpoint(v), { status, changedBy: user?.id });
      setVideos(prev => prev.filter(x => x.id !== v.id));
      if (openVideo?.id === v.id) setOpenVideo(null);
      toast(status === 'Approved' ? `${videoTitle(v)} approved.` : `${videoTitle(v)} sent back to editing.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
    } finally {
      setBusyId(null);
    }
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  const clientOpts = clients.map(c => ({ value: c.id, label: clean(c.name) }));
  const writers = members.filter(m => memberHas(m, 'Writer'));
  const editors = members.filter(m => memberHas(m, 'Editor'));
  const producers = members.filter(m => m.isActive !== false && !m.isExternal && memberHas(m, 'Producer'));   // OUT-4: the producer picker
  const sFiltersOn = !!(sClientFilter || sWriterFilter || sReviewerFilter);
  const vFiltersOn = !!(vClientFilter || vTrackFilter || vEditorFilter);
  const clearS = () => { setSClientFilter(''); setSWriterFilter(''); setSReviewerFilter(''); };
  const clearV = () => { setVClientFilter(''); setVTrackFilter(''); setVEditorFilter(''); };
  const listState = tab === 'scripts' ? scriptsState : videosState;
  const count = tab === 'scripts' ? scripts.length : videos.length;

  usePageHeader({
    title: 'Qual',
    meta: listState === 'loading' ? 'Loading'
      : tab === 'scripts' ? `${plural(count, 'script')} waiting on review` : `${plural(count, 'video')} waiting on review`,
    actions: <Button variant="quiet" icon={RefreshCw} onClick={tab === 'scripts' ? fetchScripts : fetchVideos}>Refresh</Button>,
    filters: tab === 'scripts' ? (
      <>
        <Select aria-label="Client" value={sClientFilter} onChange={e => setSClientFilter(e.target.value)}
          options={[{ value: '', label: 'All clients' }, ...clientOpts]} />
        <Select aria-label="Writer" value={sWriterFilter} onChange={e => setSWriterFilter(e.target.value)}
          options={[{ value: '', label: 'All writers' }, ...writers.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        <Select aria-label="Reviewer" value={sReviewerFilter} onChange={e => setSReviewerFilter(e.target.value)}
          options={[{ value: '', label: 'All reviewers' }, { value: 'unassigned', label: 'Unassigned' }, ...members.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        {sFiltersOn && <Button variant="quiet" icon={X} onClick={clearS}>Clear</Button>}
      </>
    ) : (
      <>
        <Select aria-label="Client" value={vClientFilter} onChange={e => setVClientFilter(e.target.value)}
          options={[{ value: '', label: 'All clients' }, ...clientOpts]} />
        <Select aria-label="Track" value={vTrackFilter} onChange={e => setVTrackFilter(e.target.value)}
          options={[{ value: '', label: 'Both tracks' }, { value: 'Live', label: 'Live' }, { value: 'AI', label: 'AI' }]} />
        <Select aria-label="Editor" value={vEditorFilter} onChange={e => setVEditorFilter(e.target.value)}
          options={[{ value: '', label: 'All editors' }, ...editors.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        {vFiltersOn && <Button variant="quiet" icon={X} onClick={clearV}>Clear</Button>}
      </>
    ),
  });

  // ── Scripts grid ────────────────────────────────────────────────────────────

  // A script waiting on qual has no production card yet: stage Writing, condition Needs review (§2.1 stageOf rule).
  const scriptGroups = [{ key: 'Writing', header: <StageChip stage="Writing" /> }];
  const scriptColumns = [
    { key: 'title', label: 'Title', width: 320, render: s => (
      <>
        {clean(s.scriptName)}
        {s.isCookingPhase2 && <> <Tag title="Cooking phase 2, a talking head built on phase 1 footage">Cooking P2</Tag></>}
      </>
    ) },
    { key: 'stage', label: 'Stage', width: 124, render: () => <StageChip stage="Writing" /> },
    { key: 'condition', label: 'Condition', width: 140, render: () => <Badge condition="Needs review" /> },
    { key: 'client', label: 'Client', width: 160, render: s => (s.client?.name ? <Tag>{clean(s.client.name)}</Tag> : <span className="ui-meta">No client</span>) },
    { key: 'writer', label: 'Writer', width: 140, render: s => (s.writer?.name ? clean(s.writer.name) : <span className="ui-meta">No writer</span>) },
    { key: 'entered', label: 'Date entered', width: 116, render: s => fmtDate(s.dateEntered) },
    { key: 'notes', label: 'Qual notes', width: 240, render: s => (s.qualNotes ? <span title={clean(s.qualNotes)}>{clean(s.qualNotes)}</span> : null) },
    { key: 'reviewer', label: 'Reviewer', width: 140, render: s => (s.qualReviewer?.name ? clean(s.qualReviewer.name) : <span className="ui-meta">Unassigned</span>) },
  ];
  const scriptActions = s => (
    <>
      {canEdit && (
        <Button variant="quiet" iconOnly icon={Check} label={`Approve ${clean(s.scriptName)}`} loading={busyId === s.id} onClick={() => setScriptStatus(s, 'Approved')} />
      )}
      {canEdit && (
        <Popover open={menuFor === s.id} onOpenChange={o => setMenuFor(o ? s.id : null)} align="end" label={`Change the status of ${clean(s.scriptName)}`}
          trigger={({ toggle, ref, props }) => (
            <Button ref={ref} variant="quiet" iconOnly icon={ArrowRightLeft} label={`Change the status of ${clean(s.scriptName)}`} onClick={toggle} {...props} />
          )}>
          <Menu label={`Set ${clean(s.scriptName)} to`} onClose={() => setMenuFor(null)} items={[
            { label: 'Approve', icon: Check, onSelect: () => setScriptStatus(s, 'Approved') },
            { label: 'Move back to in progress', icon: Undo2, onSelect: () => setScriptStatus(s, 'I/P') },
          ]} />
        </Popover>
      )}
    </>
  );

  // ── Videos grid ─────────────────────────────────────────────────────────────

  // D-120: longform episodes never sit among skit peers. Each series is its own group after the Qual stage group,
  // its episodes rows of their own (own ids), by episode number; the rest keep the API order (client, Live before AI).
  const { videoRows, videoGroups, videoGroupOf } = useMemo(() => {
    const plain = videos.filter(v => !isEpisode(v));
    const seriesOrder = [];
    const bySeries = new Map();
    for (const v of videos) {
      if (!isEpisode(v)) continue;
      if (!bySeries.has(v.scriptId)) { bySeries.set(v.scriptId, []); seriesOrder.push(v.scriptId); }
      bySeries.get(v.scriptId).push(v);
    }
    const seriesDefs = seriesOrder.map(sid => {
      const eps = bySeries.get(sid).sort((a, b) => a.episodeNumber - b.episodeNumber);
      return {
        key: `series:${sid}`,
        eps,
        header: <>{clean(eps[0].name)}<Tag>Longform</Tag><span className="ui-meta">{plural(eps.length, 'episode')} in qual</span></>,
      };
    });
    return {
      videoRows: [...plain, ...seriesDefs.flatMap(d => d.eps)],
      videoGroups: [{ key: 'Qual', header: <StageChip stage="Qual" /> }, ...seriesDefs.map(({ key, header }) => ({ key, header }))],
      videoGroupOf: v => (isEpisode(v) ? `series:${v.scriptId}` : 'Qual'),
    };
  }, [videos]);

  const videoColumns = [
    { key: 'title', label: 'Title', width: 320, render: v => videoTitle(v) },
    { key: 'stage', label: 'Stage', width: 124, render: () => <StageChip stage="Qual" /> },
    { key: 'condition', label: 'Condition', width: 140, render: () => <Badge condition="Needs review" /> },
    { key: 'client', label: 'Client', width: 160, render: v => (v.client?.name ? <Tag>{clean(v.client.name)}</Tag> : <span className="ui-meta">No client</span>) },
    { key: 'track', label: 'Track', width: 80, render: v => <Tag>{v.track}</Tag> },
    { key: 'producer', label: 'Producer', width: 140, render: v => (v.track !== 'Live' ? <span className="ui-meta">None (AI)</span>
      : v.producer?.name ? <span className={v.producerDefaulted ? 'ui-text-3' : undefined} title={v.producerDefaulted ? `${clean(v.producer.name)}, defaulted when the card was marked filmed` : undefined}>{clean(v.producer.name)}</span>
      : <span className="ui-meta">Unassigned</span>) },
    { key: 'editor', label: 'Editor', width: 140, render: v => (v.editor?.name ? clean(v.editor.name) : <span className="ui-meta">Unassigned</span>) },
    { key: 'frame', label: 'Review link', width: 120, render: v => (v.frameLink ? (
      <a className="ui ui-btn ui-btn--quiet" href={v.frameLink} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        aria-label={`Open the Frame.io review of ${videoTitle(v)}`}>
        <Icon icon={ExternalLink} />Frame.io
      </a>
    ) : <span className="ui-meta">None</span>) },
  ];
  const videoActions = v => (canEdit ? (
    <>
      {hasEditor(v)
        ? <Button variant="quiet" iconOnly icon={Check} label={`Approve ${videoTitle(v)}`} loading={busyId === v.id} onClick={() => setVideoStatus(v, 'Approved')} />
        : (
          <>
            {/* a disabled button shows no tooltip of its own: the sentence sits on its wrapper and in its name */}
            <span title={NEEDS_EDITOR}><Button variant="quiet" iconOnly icon={Check} label={`Approve ${videoTitle(v)}: ${NEEDS_EDITOR.toLowerCase()}`} disabled /></span>
            <Button variant="quiet" iconOnly icon={Pencil} label={`Edit card ${videoTitle(v)}`} onClick={() => navigate(editCardPath(v))} />
          </>
        )}
      <Button variant="quiet" iconOnly icon={Undo2} label={`Send ${videoTitle(v)} back to editing`} disabled={busyId === v.id} onClick={() => setVideoStatus(v, 'Editing')} />
    </>
  ) : null);

  return (
    <div className="ui ui-page ui-page--flush">
      <div className="ui-inset">
        <Tabs label="Qual queue" value={tab} onChange={setTab}
          items={TABS.map(t => ({ ...t, count: t.id === tab && count > 0 && listState === 'data' ? count : undefined }))} />
      </div>

      {tab === 'scripts' && (
        <div className="ui-work">
          <Grid key="scripts"
            label="Scripts waiting on review"
            columns={scriptColumns} rows={scripts} rowKey={r => r.id} groups={scriptGroups} groupOf={() => 'Writing'}
            density="medium" storageKey="hf-grid:qual-scripts" selectedKey={openScriptId}
            onOpen={s => setOpenScriptId(s.id)} rowActions={canEdit ? scriptActions : undefined}
            state={scriptsState}
            empty={sFiltersOn
              ? { message: 'No script matches these filters. Clear them to see every script waiting on review.', action: { label: 'Clear filters', icon: X, onClick: clearS } }
              : { message: 'No script is waiting on review. A script shows up here when its writer sends it for qual.' }}
            error={{ title: 'The scripts did not load', message: 'Check your connection, then try again.', onRetry: fetchScripts }} />
          {openScriptId && (
            <ScriptPanel key={openScriptId} scriptId={openScriptId} canEdit={canEdit} onClose={() => setOpenScriptId(null)}
              onStatusChanged={id => setScripts(prev => prev.filter(s => s.id !== id))} />
          )}
        </div>
      )}

      {tab === 'videos' && (
        <div className="ui-work">
          <Grid key="videos"
            label="Videos waiting on review"
            columns={videoColumns} rows={videoRows} rowKey={r => r.id} groups={videoGroups} groupOf={videoGroupOf}
            density="medium" storageKey="hf-grid:qual-videos" selectedKey={openVideo?.id}
            onOpen={setOpenVideo} rowActions={canEdit ? videoActions : undefined}
            state={videosState}
            empty={vFiltersOn
              ? { message: 'No video matches these filters. Clear them to see every video waiting on review.', action: { label: 'Clear filters', icon: X, onClick: clearV } }
              : { message: 'No video is waiting on review. A cut shows up here when its editor sends it for qual.' }}
            error={{ title: 'The videos did not load', message: 'Check your connection, then try again.', onRetry: fetchVideos }} />
          {openVideo && (
            <VideoPanel key={openVideo.id} video={openVideo} canEdit={canEdit} onClose={() => setOpenVideo(null)} producers={producers}
              onStatusChanged={id => setVideos(prev => prev.filter(v => v.id !== id))}
              onChanged={(id, patch) => { setVideos(prev => prev.map(v => (v.id === id ? { ...v, ...patch } : v))); setOpenVideo(o => (o && o.id === id ? { ...o, ...patch } : o)); }} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Script panel: summary note, the script's scenes (editable for reviewers), per-scene qual notes ──

function ScriptPanel({ scriptId, canEdit, onClose, onStatusChanged }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [script,        setScript]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [qualNotes,     setQualNotes]     = useState('');
  const [draftScenes,   setDraftScenes]   = useState(null);
  const [dirty,         setDirty]         = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/scripts/${scriptId}`)
      .then(r => {
        setScript(r.data);
        setQualNotes(r.data.qualNotes || '');
        setDraftScenes(r.data.scriptBody?.scenes ? r.data.scriptBody.scenes.map(s => ({ ...s })) : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scriptId]);

  const setScene = (i, key, value) => {
    setDraftScenes(prev => { const next = [...prev]; next[i] = { ...next[i], [key]: value }; return next; });
    setDirty(true);
  };

  const buildPayload = () => {
    const payload = { qualNotes };
    if (draftScenes && script?.scriptBody) payload.scriptBody = { ...script.scriptBody, scenes: draftScenes };
    return payload;
  };

  async function handleSave() {
    setSaving(true);
    try {
      await api.patch(`/api/scripts/${scriptId}`, buildPayload());
      setDirty(false);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The notes did not save. Try again.', { tone: 'danger' });
    } finally { setSaving(false); }
  }

  // Saves the notes and scenes first, then changes the status (as before).
  async function handleStatusChange(newStatus) {
    setTransitioning(true);
    try {
      await api.patch(`/api/scripts/${scriptId}`, buildPayload());
      await api.patch(`/api/scripts/${scriptId}/status`, { status: newStatus });
      onStatusChanged(scriptId);
      onClose();
      const name = clean(script?.scriptName) || 'The script';
      toast(newStatus === 'Approved' ? `${name} approved.` : newStatus === 'Backburner' ? `${name} moved to the backburner.` : `${name} sent back for revision.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The status did not change. Try again.', { tone: 'danger' });
      setTransitioning(false);
    }
  }

  const cols = FORMAT_COLS[script?.scriptBody?.format] || [];
  const ready = canEdit && !loading && script;

  return (
    <ExpandedRowPanel open title={script ? clean(script.scriptName) : loading ? 'Loading the script' : 'Script not found'}
      onClose={onClose} label="Script qual review"
      footer={ready && (
        <>
          <Button variant="primary" icon={Check} disabled={transitioning} onClick={() => handleStatusChange('Approved')}>Approve</Button>
          <Button variant="danger" icon={Undo2} disabled={transitioning} onClick={() => handleStatusChange('Needs Revision')}>Needs revision</Button>
          <Button variant="quiet" disabled={transitioning} onClick={() => handleStatusChange('Backburner')}>Backburner</Button>
        </>
      )}>
      {loading ? (
        <div className="ui-stack"><Skeleton width="60%" /><Skeleton width="40%" /><Skeleton variant="block" height={120} /></div>
      ) : !script ? (
        <p className="ui-p ui-dense ui-text-2">This script could not be loaded. It may have been deleted; refresh the list.</p>
      ) : (
        <div className="ui-stack">
          <dl className="ui-kv">
            <dt>Stage</dt><dd><StageChip stage="Writing" /></dd>
            <dt>Condition</dt><dd><Badge condition="Needs review" /></dd>
            <dt>Client</dt><dd>{script.client?.name ? clean(script.client.name) : 'No client'}</dd>
            <dt>Writer</dt><dd>{script.writer?.name ? clean(script.writer.name) : 'No writer'}</dd>
            <dt>Date entered</dt><dd>{fmtDate(script.dateEntered)}</dd>
            <dt>Reviewer</dt><dd>{script.qualReviewer?.name ? clean(script.qualReviewer.name) : 'Unassigned'}</dd>
          </dl>
          {canEdit && (
            <span>
              <Button variant="secondary" icon={FileText} onClick={() => { onClose(); navigate(`/scripts?script=${scriptId}`); }}>Open in editor</Button>
            </span>
          )}

          <Textarea label="Summary note" rows={3} value={qualNotes} readOnly={!canEdit}
            hint="Shown as a banner in the script editor. For a correction on one scene, use that scene's qual note."
            placeholder="Overall feedback for the writer"
            onChange={e => { setQualNotes(e.target.value); setDirty(true); }}
            onBlur={() => { if (dirty) handleSave(); }} />
          {canEdit && dirty && (
            <span><Button variant="secondary" loading={saving} onClick={handleSave}>Save changes</Button></span>
          )}

          {draftScenes ? draftScenes.map((scene, i) => (
            <section key={i} className="ui-section" aria-labelledby={`qual-scene-${i}`}>
              <h3 className="ui-heading" id={`qual-scene-${i}`}>Scene {i + 1}</h3>
              {canEdit ? (
                <>
                  {cols.map(col => (
                    <Textarea key={col.key} label={col.label} rows={rowsFor(scene[col.key])} value={scene[col.key] || ''}
                      onChange={e => setScene(i, col.key, e.target.value)} />
                  ))}
                  <Textarea label="Qual note" hint="Shows on this scene in the script editor." rows={2} placeholder="Note for this scene"
                    value={scene.qualNotes || ''} onChange={e => setScene(i, 'qualNotes', e.target.value)}
                    onBlur={() => { if (dirty) handleSave(); }} />
                </>
              ) : (
                <dl className="ui-kv">
                  {cols.map(col => <ScenePair key={col.key} label={col.label} value={scene[col.key]} />)}
                  <ScenePair label="Qual note" value={scene.qualNotes} />
                </dl>
              )}
            </section>
          )) : (
            <p className="ui-p ui-dense ui-text-3">No script content attached.</p>
          )}
        </div>
      )}
    </ExpandedRowPanel>
  );
}

function ScenePair({ label, value }) {
  return <><dt>{label}</dt><dd className="ui-prewrap">{value ? clean(value) : ''}</dd></>;
}

// ── Video panel: the cut (thumbnail, review, downloads, archive, share links) or an upload, Frame.io link ──

function VideoPanel({ video, canEdit, onClose, onStatusChanged, onChanged, producers = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const uploads = useUploads();
  const [transitioning,    setTransitioning]    = useState(false);
  const [videoAsset,       setVideoAsset]       = useState(null);
  const [assetLoading,     setAssetLoading]     = useState(true);
  const [archivedVersions, setArchivedVersions] = useState([]);
  const [legacyOpen,       setLegacyOpen]       = useState(false);
  const [zipJob,           setZipJob]           = useState(null);
  const zipPollRef = useRef(null);

  const roles = user?.roles || [];
  const isAdmin     = roles.some(r => ['Admin', 'Power User'].includes(r));
  const isShootLead = roles.includes('Producer');
  const isEditor    = roles.includes('Editor');
  const canDelete   = isAdmin || isShootLead;
  const canUpload   = isAdmin || isShootLead || isEditor;
  // OUT-4 (C5 §4, rulings A, K): a Live card's producer, set (or a defaulted one confirmed) by Admin, Producer, Power User,
  // Manager; PATCH /api/live-production/:id { producerId } saves at once.
  const canPickProducer = canEdit && video.track === 'Live' && canSetProducer(user);
  const [producerBusy, setProducerBusy] = useState(false);
  const [producerDetails, setProducerDetails] = useState(false);
  async function setProducer(id) {
    if (!id) return;
    setProducerBusy(true);
    try {
      const res = await api.patch(`/api/live-production/${video.id}`, { producerId: id });
      const m = producers.find(x => x.id === id);
      onChanged?.(video.id, { producerId: id, producer: res.data.producer || (m ? { id, name: m.name } : video.producer), producerDefaulted: false });
      toast(id === video.producer?.id ? `${clean(video.producer?.name) || 'The producer'} confirmed as producer.` : 'Producer saved.');
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The producer was not saved. Try again.', { tone: 'danger' });
    } finally { setProducerBusy(false); }
  }
  const producerOptions = [...(video.producer && !producers.some(m => m.id === video.producer.id) ? [{ value: video.producer.id, label: clean(video.producer.name) }] : []),
    ...producers.map(m => ({ value: m.id, label: clean(m.name) }))];

  const prodParam = video.track === 'Live' ? `liveProductionId=${video.id}` : `aiProductionId=${video.id}`;
  const fetchArchived = useCallback(() => {
    api.get(`/api/video-assets/archived?${prodParam}`).then(r => setArchivedVersions(r.data)).catch(() => {});
  }, [prodParam]);

  useEffect(() => {
    api.get(`/api/video-assets?${prodParam}`)
      .then(r => setVideoAsset(r.data))
      .catch(() => {})
      .finally(() => setAssetLoading(false));
    fetchArchived();
  }, [prodParam, fetchArchived]);

  useEffect(() => () => { if (zipPollRef.current) clearInterval(zipPollRef.current); }, []);

  // An upload for this card that finishes while the panel is open becomes the current video (as VideoUploader's
  // onUploadComplete did). The confirm call archives any version it replaced, so the archived list is re-read too.
  useUploadDone(item => {
    if (item.meta.productionId !== video.id) return;
    setVideoAsset(item.result);
    fetchArchived();
  });
  const upload = [...uploads.items].reverse().find(i => i.meta.productionId === video.id && i.status !== 'done');

  async function handleStatusChange(newStatus) {
    setTransitioning(true);
    try {
      await api.patch(statusEndpoint(video), { status: newStatus, changedBy: user?.id });
      onStatusChanged(video.id);
      onClose();
      toast(newStatus === 'Approved' ? `${videoTitle(video)} approved.` : `${videoTitle(video)} sent back to editing.`);
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The stage did not change. Try again.', { tone: 'danger' });
      setTransitioning(false);
    }
  }

  function goReview(assetId) {
    const p = new URLSearchParams(location.search);
    p.set('video', video.id);
    const returnTo = `${location.pathname}?${p.toString()}`;
    onClose();
    navigate(`/review/${assetId}`, { state: { returnTo, returnLabel: RETURN_LABEL, returnVideo: video } });
  }

  async function handleDownload() {
    try {
      const res = await api.get(`/api/video-assets/${videoAsset.id}/download`);
      window.open(res.data.url, '_blank');
    } catch {
      toast('The download did not start. Try again.', { tone: 'danger' });
    }
  }

  async function handleZipDownload() {
    try {
      setZipJob({ status: 'processing' });
      const res = await api.post('/api/downloads/zip', { videoAssetIds: [videoAsset.id] });
      const { jobId } = res.data;
      zipPollRef.current = setInterval(async () => {
        try {
          const poll = await api.get(`/api/downloads/zip/${jobId}`);
          if (poll.data.status === 'complete') {
            clearInterval(zipPollRef.current);
            setZipJob({ status: 'complete', downloadUrl: poll.data.downloadUrl });
          } else if (poll.data.status === 'failed') {
            clearInterval(zipPollRef.current);
            setZipJob(null);
            toast('The zip could not be made. Try again.', { tone: 'danger' });
          }
        } catch {
          clearInterval(zipPollRef.current);
          setZipJob(null);
        }
      }, 3000);
    } catch {
      setZipJob(null);
      toast('The zip download did not start. Try again.', { tone: 'danger' });
    }
  }

  // R-2: removing a video always archives it; it is never deleted from storage.
  async function handleArchiveVideo() {
    const ok = await confirm({
      title: 'Archive this video?',
      body: `${clean(videoAsset.fileName || 'The video')} is archived, never deleted from storage. The file and its comments are kept; archived versions are listed in the review page's version menu, where they can be restored. Only an Admin can permanently delete an archive.`,
      confirmLabel: 'Archive video',
    });
    if (!ok) return;
    try {
      const res = await api.delete(`/api/video-assets/${videoAsset.id}`);
      setVideoAsset(null);
      if (res.data.archived) fetchArchived();
      toast('Video archived.');
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The video was not archived. Try again.', { tone: 'danger' });
    }
  }

  const frameButton = video.frameLink && (
    <span>
      <a className="ui ui-btn ui-btn--secondary" href={video.frameLink} target="_blank" rel="noreferrer"><Icon icon={ExternalLink} />Review in Frame.io</a>
    </span>
  );

  return (
    <ExpandedRowPanel open title={videoTitle(video)} onClose={onClose} label="Video qual review"
      footer={canEdit && (hasEditor(video) ? (
        <>
          <Button variant="primary" icon={Check} disabled={transitioning} onClick={() => handleStatusChange('Approved')}>Approve</Button>
          <Button variant="secondary" icon={Undo2} disabled={transitioning} onClick={() => handleStatusChange('Editing')}>Back to editing</Button>
        </>
      ) : (
        <>
          <span title={NEEDS_EDITOR}><Button variant="primary" icon={Check} disabled aria-describedby="qual-needs-editor">Approve</Button></span>
          <Button variant="secondary" icon={Pencil} onClick={() => { onClose(); navigate(editCardPath(video)); }}>Edit card</Button>
          <Button variant="secondary" icon={Undo2} disabled={transitioning} onClick={() => handleStatusChange('Editing')}>Back to editing</Button>
          <p className="ui-meta ui-qual__needs" id="qual-needs-editor">{NEEDS_EDITOR}.</p>
        </>
      ))}>
      <div className="ui-stack">
        <dl className="ui-kv">
          <dt>Stage</dt><dd><StageChip stage="Qual" /></dd>
          <dt>Condition</dt><dd><Badge condition="Needs review" /></dd>
          <dt>Client</dt><dd>{video.client?.name ? clean(video.client.name) : 'No client'}</dd>
          <dt>Track</dt><dd>{video.track}</dd>
          {video.track === 'Live' && (<>
            <dt>Producer</dt>
            <dd>
              {canPickProducer
                ? <Select aria-label="Producer" data-testid="qual-producer" value={video.producer?.id || ''} disabled={producerBusy} placeholder="Set the producer"
                    options={producerOptions} onChange={e => setProducer(e.target.value)} />
                : video.producer?.name ? clean(video.producer.name) : 'Unassigned'}
            </dd>
          </>)}
          <dt>Editor</dt><dd>{video.editor?.name ? clean(video.editor.name) : 'Unassigned'}</dd>
        </dl>
        {video.track === 'Live' && video.producerDefaulted && video.producer && (
          <div className="ui-stack ui-stack--tight" data-testid="producer-defaulted">
            <button type="button" className="ui-disclosure ui-output__disclosure" aria-expanded={producerDetails} aria-controls="qual-producer-details" onClick={() => setProducerDetails(o => !o)}>
              <Icon icon={ChevronDown} />Details
            </button>
            {producerDetails && (
              <div className="ui-cluster" id="qual-producer-details">
                <span className="ui-dense">Producer: {clean(video.producer.name)} (defaulted)</span>
                {canPickProducer && <Button variant="secondary" loading={producerBusy} data-testid="producer-confirm" onClick={() => setProducer(video.producer.id)}>Confirm</Button>}
              </div>
            )}
          </div>
        )}

        <section className="ui-section" aria-labelledby="qual-video">
          <h3 className="ui-heading" id="qual-video">Video</h3>
          {assetLoading ? (
            <Skeleton variant="block" height={180} label="Loading the video" />
          ) : videoAsset ? (
            <>
              <div className="ui ui-monitor">
                <div className="ui-monitor__frame ui-monitor__frame--16x9">
                  {videoAsset.thumbnailUrl
                    ? <img className="ui-cover" src={videoAsset.thumbnailUrl} alt="" />
                    : <Skeleton variant="block" label="Thumbnail generating" />}
                </div>
              </div>
              <span className="ui-meta">
                {clean(videoAsset.fileName)}{(videoAsset.openCommentCount ?? 0) > 0 ? `, ${videoAsset.openCommentCount} open` : ''}
              </span>
              <div className="ui-cluster">
                <Button variant="secondary" icon={Play} onClick={() => goReview(videoAsset.id)}>Open review</Button>
                <ShareLinkManager videoAssetId={videoAsset.id} />
                <Button variant="quiet" icon={Download} onClick={handleDownload}>Download video</Button>
                {zipJob?.status !== 'complete' && (
                  <Button variant="quiet" icon={Download} loading={zipJob?.status === 'processing'} onClick={handleZipDownload}>
                    {zipJob?.status === 'processing' ? 'Preparing zip' : 'Download as zip'}
                  </Button>
                )}
                {zipJob?.status === 'complete' && zipJob.downloadUrl && (
                  <a className="ui ui-btn ui-btn--primary" href={zipJob.downloadUrl} target="_blank" rel="noreferrer"><Icon icon={Download} />Download zip</a>
                )}
                {(canDelete || videoAsset.uploadedBy === user?.id) && (
                  <Button variant="danger" icon={Archive} onClick={handleArchiveVideo}>Archive video</Button>
                )}
              </div>
            </>
          ) : upload ? (
            <p className="ui-p ui-dense ui-text-2" role="status">
              {upload.status === 'failed'
                ? `${clean(upload.name)} did not upload. Retry it from the upload sheet.`
                : `Uploading ${clean(upload.name)}, ${Math.round(upload.progress * 100)}%. You can keep working; the upload sheet shows its progress.`}
            </p>
          ) : canUpload ? (
            <VideoDrop video={video} onFile={file => uploads.enqueueVideo({
              file,
              clientId: video.client?.id,
              liveProductionId: video.track === 'Live' ? video.id : undefined,
              aiProductionId: video.track === 'AI' ? video.id : undefined,
            })} />
          ) : (
            <p className="ui-p ui-dense ui-text-3">No video uploaded yet.</p>
          )}
          {archivedVersions.length > 0 && (
            <span>
              <Button variant="quiet" icon={Archive} onClick={() => goReview(archivedVersions[0].id)}>
                {plural(archivedVersions.length, 'archived version')}
              </Button>
            </span>
          )}
        </section>

        {video.frameLink ? (
          videoAsset ? (
            <div className="ui-stack ui-stack--tight">
              <span>
                <button type="button" className="ui-disclosure ui-dense ui-text-2" aria-expanded={legacyOpen} onClick={() => setLegacyOpen(v => !v)}>
                  <Icon icon={ChevronDown} />Legacy Frame.io link
                </button>
              </span>
              {legacyOpen && frameButton}
            </div>
          ) : frameButton
        ) : !videoAsset && !assetLoading ? (
          <p className="ui-p ui-dense ui-text-3">No Frame.io review link is attached to this video.</p>
        ) : null}
      </div>
    </ExpandedRowPanel>
  );
}

// Drop a video here or choose one; it goes to the global Upload sheet straight away and uploads in the background.
function VideoDrop({ video, onFile }) {
  const toast = useToast();
  const input = useRef(null);
  const [over, setOver] = useState(false);

  function take(file) {
    if (!file) return;
    const ok = VIDEO_EXT.test(file.name) || (file.type || '').startsWith('video/');
    if (!ok) { toast('Choose a video file: MP4, MOV, WebM or AVI.', { tone: 'warn' }); return; }
    onFile(file);
  }

  return (
    <div className="ui-dropzone" data-drop={over || undefined}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); }}>
      <span className="ui-dense ui-text-2">Drop the cut of {videoTitle(video)} here, or choose the file. MP4, MOV, WebM or AVI.</span>
      <Button variant="secondary" icon={Upload} onClick={() => input.current?.click()}>Choose video</Button>
      <input ref={input} type="file" accept={ACCEPTED_EXT} hidden onChange={e => { take(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}
